/* eslint-disable react/display-name */
/*
 *  ************************************************************************
 *  *  © [2015 - 2020] Quintype Technologies India Private Limited
 *  *  All Rights Reserved.
 *  *************************************************************************
 */
import get from "lodash/get";
import set from "lodash/set";
import React from "react";
import { shallowEqual, useSelector } from "react-redux";

const WithArrowConfig =
  (Component) =>
  ({ collection = {}, config = {}, index, updateAdAndWidgetComponent }) => {
    const pageType = useSelector((state) => get(state, ["qt", "pageType"], ""), shallowEqual);
    const supportedPages = {
      "home-page": "home",
      "section-page": "section",
      "collection-page": "section",
      "tag-page": "tag",
      "author-page": "author",
      "pagebuilder-section-preview-page": "section",
      "pagebuilder-collection-preview-page": "section",
      "pagebuilder-story-preview-page": "story",
      "authors-page": "authors",
      "magazine-landing-page": "magazine-landing",
      "issue-landing-page": "issue-landing",
      "magazine-archive-page": "archive",
      "": "",
    };

    const magazinePage = pageType === "magazine-landing-page" || pageType === "issue-landing-page";
    const updatedPath = !magazinePage ? [supportedPages[pageType]] : ["magazine", supportedPages[pageType]];
    const enableDarkMode = useSelector((state) => get(state, ["header", "isDarkModeEnabled"], false));
    const { "pagebuilder-config": pbconfig} = useSelector((state) =>
      get(state, ["qt", "config"], {})
    );
    const theme = enableDarkMode
      ? get(pbconfig, [...updatedPath, "pageSetting", "darkBgColor"], "")
      : get(pbconfig, [...updatedPath, "pageSetting", "bgColor"], "");

    const fallbackImageUrl = get(pbconfig, ["general", "fallbackImage", "url"], "");

    const updateConfig = (config = {}) => {
      const pbRowsConfig = useSelector((state) =>
        get(state, ["qt", "config", "pagebuilder-config", "general", "rows"], {})
      );
      const {
        enablePremiumStoryIcon = false,
        iconColor,
        iconStyle = "star",
      } = get(pbRowsConfig, ["premiumStoryIcon"], {});
      const premiumStoryIconConfig = { enablePremiumStoryIcon, iconColor, iconStyle };
      const collectionNameTemplate = get(pbRowsConfig, ["collectionNameTemplate"], "");
      const borderColor = enableDarkMode ? get(pbRowsConfig, ["sectionTag", "darkColor"],  get(pbRowsConfig, ["sectionTag", "color"], "#3cab92")):   get(pbRowsConfig, ["sectionTag", "color"], ""); // borderColor to change sectionTag color. TODO: rename to sectionTagColor
      const collectionNameBorderColor = enableDarkMode? get(pbRowsConfig, ["titleStyle", "darkUnderLineColor"], get(pbRowsConfig, ["titleStyle", "underLineColor"], "#3cab92")): get(pbRowsConfig, ["titleStyle", "underLineColor"], "");
      const supportedPagesForTitle = ["author-page", "tag-page"];

      const pages = supportedPagesForTitle.includes(pageType);

      const { enableLocalization = false, localizedElements = {} } = useSelector((state) =>
        get(state, ["qt", "config", "pagebuilder-config", "general", "localization"], {})
      );

      const { months, meridiem, publishTime, buttonLabels = {} } = localizedElements;
      const { timeToRead = "", keyEvents } = buttonLabels;
      const localizationConfig = enableLocalization
        ? {
            localizedTimeToRead: timeToRead,
            localizedMonths: months,
            localizedMeridiem: meridiem,
            localizedPublishTime: publishTime,
            localizedKeyEvents: keyEvents,
          }
        : {};

      // Arrow needs the config values like sectionTagTemplate directly under config prop.
      // TODO: May need to flatten the rowConfig sometime.
      const sectionTagTemplate = get(
        pbRowsConfig,
        ["sectionTag", "template"],
        get(pbRowsConfig, ["sectionTagTemplate"], "solid")
      );
      const configObj = { ...config };
      const collectionConfigObj = {...collection.config}
      if (enableDarkMode === true ) {
        configObj.darkTheme ? set(configObj, ["theme"], get(config, ["darkTheme"], "#1d1d1d")): set(configObj, ["theme"], theme || "#1d1d1d") ;
        collectionConfigObj && set(collectionConfigObj, ["theme"], get(collectionConfigObj, ["darkTheme"], "#1d1d1d"));
        configObj.darkCustomBulletColor && set(configObj, ["customBulletColor"], get(configObj, ["darkCustomBulletColor"], "#1d1d1d"));
      }

      const updatedConfig = {
        theme,
        ...configObj,
        ...collectionConfigObj, // collection.config is available only from section page when it is collectionOfStories.
        sectionTagTemplate,
        collectionNameTemplate,
        borderColor,
        collectionNameBorderColor,
        fallbackImageUrl,
        ...premiumStoryIconConfig,
        localizationConfig,
        ...(pages && { showRowTitle: false }),
      };


      if (updateAdAndWidgetComponent) {
        return updateAdAndWidgetComponent(updatedConfig);
      }

      return updatedConfig;
    };

    return <Component collection={collection} config={updateConfig(config)} index={index} enableDarkMode={enableDarkMode} />;
  };
export default WithArrowConfig;
